import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { toPng, toSvg } from 'html-to-image';
import { toast } from 'react-toastify';
import logger from '/imports/startup/client/logger';
import {
  PresentationDropdownItemType,
} from 'bigbluebutton-html-plugin-sdk/dist/cjs/extensible-areas/presentation-dropdown-item/enums';

import Styled from './styles';
import BBBMenu from '/imports/ui/components/common/menu/component';
import TooltipContainer from '/imports/ui/components/common/tooltip/container';
import { ACTIONS } from '/imports/ui/components/layout/enums';
import deviceInfo from '/imports/utils/deviceInfo';
import browserInfo from '/imports/utils/browserInfo';
import AppService, { getSlidesData } from '/imports/ui/components/app/service';
import { getSettingsSingletonInstance } from '/imports/ui/services/settings';
import SvgIcon from '/imports/ui/components/common/icon-svg/component';
import FullscreenService from '/imports/ui/components/common/fullscreen-button/service';
import Spinner from '../../common/spinner/component';
import jsPDF from "jspdf";
import { courseSelect } from '../../app/context';

const intlMessages = defineMessages({
  downloading: {
    id: 'app.presentation.options.downloading',
    description: 'Downloading label',
    defaultMessage: 'Downloading...',
  },
  downloaded: {
    id: 'app.presentation.options.downloaded',
    description: 'Downloaded label',
    defaultMessage: 'Current presentation was downloaded',
  },
  downloadFailed: {
    id: 'app.presentation.options.downloadFailed',
    description: 'Downloaded failed label',
    defaultMessage: 'Could not download current presentation',
  },
  fullscreenLabel: {
    id: 'app.presentation.options.fullscreen',
    description: 'Fullscreen label',
    defaultMessage: 'Fullscreen',
  },
  exitFullscreenLabel: {
    id: 'app.presentation.options.exitFullscreen',
    description: 'Exit fullscreen label',
    defaultMessage: 'Exit fullscreen',
  },
  fullscreenAppLabel: {
    id: 'app.navBar.optionsDropdown.fullscreenLabel',
    description: 'Make fullscreen option label',
  },
  exitFullscreenAppLabel: {
    id: 'app.navBar.optionsDropdown.exitFullscreenLabel',
    description: 'Exit fullscreen option label',
  },
  minimizePresentationLabel: {
    id: 'app.presentation.options.minimize',
    description: 'Minimize presentation label',
    defaultMessage: 'Minimize',
  },
  optionsLabel: {
    id: 'app.navBar.optionsDropdown.optionsLabel',
    description: 'Options button label',
    defaultMessage: 'Options',
  },
  snapshotLabel: {
    id: 'app.presentation.options.snapshot',
    description: 'Snapshot of current slide label',
    defaultMessage: 'Snapshot of current slide',
  },
  whiteboardLabel: {
    id: 'app.shortcut-help.whiteboard',
    description: 'used for aria whiteboard options button label',
    defaultMessage: 'Whiteboard',
  },
  hideToolsDesc: {
    id: 'app.presentation.presentationToolbar.hideToolsDesc',
    description: 'Hide toolbar label',
  },
  showToolsDesc: {
    id: 'app.presentation.presentationToolbar.showToolsDesc',
    description: 'Show toolbar label',
  },
});

const propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  allowSnapshotOfCurrentSlide: PropTypes.bool,
  handleToggleFullscreen: PropTypes.func.isRequired,
  isFullscreen: PropTypes.bool,
  elementName: PropTypes.string,
  fullscreenRef: PropTypes.instanceOf(Element),
  meetingName: PropTypes.string,
  isIphone: PropTypes.bool,
  elementId: PropTypes.string,
  elementGroup: PropTypes.string,
  currentElement: PropTypes.string,
  currentGroup: PropTypes.string,
  layoutContextDispatch: PropTypes.func.isRequired,
  isRTL: PropTypes.bool,
  setIsPdfLoading: PropTypes.func.isRequired,
  tldrawAPI: PropTypes.shape({
    getSvg: PropTypes.func.isRequired,
    getCurrentPageShapes: PropTypes.func.isRequired,
  }),
  presentationDropdownItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  })).isRequired,
};

const PresentationMenu = (props) => {
  const Settings = getSettingsSingletonInstance();
  const {
    intl,
    isFullscreen = false,
    elementId = '',
    elementName = '',
    elementGroup = '',
    currentElement = '',
    currentGroup = '',
    fullscreenRef = null,
    tldrawAPI = null,
    handleToggleFullscreen,
    layoutContextDispatch,
    meetingName = '',
    isIphone = false,
    isRTL = Settings.application.isRTL,
    isToolbarVisible,
    setIsToolbarVisible,
    allowSnapshotOfCurrentSlide = false,
    presentationDropdownItems,
    slideNum,
    currentUser,
    whiteboardId,
    persistShape,
    hasWBAccess,
    currentSlideNum,
    presentationId,
    isPdfLoading,
    setIsPdfLoading,
  } = props;

  const [state, setState] = useState({
    hasError: false,
    loading: false,
  });

  const [isFullscreenApp, setIsFullscreenApp] = useState(false);
  const courseDetails = courseSelect((state) => state);

  const courseName = courseDetails?.course?.courseDetail?.courseName

  const lessonName = courseDetails?.course?.courseDetail?.lesson

  const onFullscreenAppChange = useCallback(() => {
    const newIsFullscreen = FullscreenService.isFullScreen(document.documentElement);
    setIsFullscreenApp((prev) => {
      if (prev !== newIsFullscreen) return newIsFullscreen;
      return prev;
    });
  }, []);

  useEffect(() => {
    document.documentElement.addEventListener('fullscreenchange', onFullscreenAppChange);

    return () => {
      document.documentElement.removeEventListener('fullscreenchange', onFullscreenAppChange);
    };
  }, [onFullscreenAppChange]);

  // const { isIphone } = deviceInfo;
  const { isSafari, isValidSafariVersion } = browserInfo;

  const noIOSFullscreen = !!(((isSafari && !isValidSafariVersion) || isIphone));
  // const noIOSFullscreen = false
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const toastId = useRef('presentation-menu-toast');
  const presentationToastId = useRef('presentation-download-toast');
  const dropdownRef = useRef(null);

  const formattedLabel = (fullscreen) => (fullscreen
    ? intl.formatMessage(intlMessages.exitFullscreenLabel)
    : intl.formatMessage(intlMessages.fullscreenLabel)
  );

  const formatteAppLabel = (fullscreen) => (fullscreen
    ? intl.formatMessage(intlMessages.exitFullscreenAppLabel)
    : intl.formatMessage(intlMessages.fullscreenAppLabel)
  );

  const formattedVisibilityLabel = (visible) => (visible
    ? intl.formatMessage(intlMessages.hideToolsDesc)
    : intl.formatMessage(intlMessages.showToolsDesc)
  );

  const extractShapes = (savedState) => {
    let data;

    // Check if savedState is a string (JSON) or an object
    if (typeof savedState === 'string') {
      try {
        data = JSON.parse(savedState);
      } catch (e) {
        console.error('Error parsing JSON:', e);
        return {};
      }
    } else if (typeof savedState === 'object' && savedState !== null) {
      data = savedState;
    } else {
      console.error('Invalid savedState type:', typeof savedState);
      return {};
    }

    // Check if 'records' key exists and extract shapes into an object keyed by shape ID
    if (data && data.records) {
      return data.records.reduce((acc, record) => {
        if (record.typeName === 'shape') {
          acc[record.id] = record;
        }
        return acc;
      }, {});
    }

    return {};
  };

  const handleFileInput = (event) => {
    const fileInput = event.target;
    const file = fileInput.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;
        const dataObj = extractShapes(JSON.parse(fileContent));
        const dataArray = Object.values(dataObj);
        dataArray.forEach((originalShape) => {
          const shape = {
            ...originalShape,
            parentId: `page:${slideNum}`,
            meta: {
              ...originalShape.meta,
              createdBy: currentUser.userId,
            },
          };
          persistShape(shape, whiteboardId, currentUser.isModerator);
        });
      };
      reader.readAsText(file);

      // Reset the file input
      fileInput.value = '';
    }
  };

  // const handleFileClick = () => {
  //   const fileInput = document.getElementById('hiddenFileInput');
  //   if (fileInput) {
  //     fileInput.click();
  //   } else {
  //     console.error('File input not found');
  //   }
  // };

  function renderToastContent() {
    const { loading, hasError } = state;

    let icon = loading ? 'blank' : 'check';
    if (hasError) icon = 'circle_close';

    return (
      <Styled.Line>
        <Styled.ToastText>
          <span>
            {loading && !hasError && intl.formatMessage(intlMessages.downloading)}
            {!loading && !hasError && intl.formatMessage(intlMessages.downloaded)}
            {!loading && hasError && intl.formatMessage(intlMessages.downloadFailed)}
          </span>
        </Styled.ToastText>
        <Styled.StatusIcon>
          <Styled.ToastIcon
            done={!loading && !hasError}
            error={hasError}
            loading={loading}
            iconName={icon}
          />
        </Styled.StatusIcon>
      </Styled.Line>
    );
  }


  function getAvailableOptions() {
    const menuItems = [];

    const { handleToggleFullscreenApp } = props

    const ALLOW_FULLSCREEN = window.meetingClientSettings.public.app.allowFullscreen;

    if (noIOSFullscreen || !ALLOW_FULLSCREEN) return null;

    if (!isIphone) {
      // Show only one if either fullscreen mode is active
      if (isFullscreen) {
        menuItems.push({
          key: 'list-item-fullscreen',
          dataTest: 'exitFullscreenWhiteboard',
          label: formattedLabel(true),
          // icon: 'exit_fullscreen',
          customIcon: <SvgIcon iconName='exit_fullscreen' />,
          onClick: () => {
            handleToggleFullscreen(fullscreenRef);
            const newElement = (elementId === currentElement) ? '' : elementId;
            const newGroup = (elementGroup === currentGroup) ? '' : elementGroup;

            layoutContextDispatch({
              type: ACTIONS.SET_FULLSCREEN_ELEMENT,
              value: {
                element: newElement,
                group: newGroup,
              },
            });
          },
        });
      } else if (isFullscreenApp) {
        menuItems.push({
          key: 'list-item-fullscreen-app',
          dataTest: 'exitFullscreenPresentation',
          label: formatteAppLabel(true),
          // icon: 'exit_fullscreen',
          customIcon: <SvgIcon iconName='exit_fullscreen' />,
          onClick: () => {
            handleToggleFullscreenApp();
          },
        });
      } else {
        // Both are false — show both "Enter Fullscreen" options
        menuItems.push(
          {
            key: 'list-item-fullscreen',
            dataTest: 'presentationFullscreen',
            label: formattedLabel(false),
            // icon: 'fullscreen',
            customIcon: <SvgIcon iconName='fullscreen_presentation' />,
            onClick: () => {
              handleToggleFullscreen(fullscreenRef);
              const newElement = (elementId === currentElement) ? '' : elementId;
              const newGroup = (elementGroup === currentGroup) ? '' : elementGroup;

              layoutContextDispatch({
                type: ACTIONS.SET_FULLSCREEN_ELEMENT,
                value: {
                  element: newElement,
                  group: newGroup,
                },
              });
            },
          },
          {
            key: 'list-item-fullscreen-app',
            dataTest: 'whiteboardFullscreen',
            label: formatteAppLabel(false),
            // icon: 'fullscreen',
            customIcon: <SvgIcon iconName='fullscreen' />,
            onClick: () => {
              handleToggleFullscreenApp();
            },
          }
        );
      }
    }


    // if (!isIphone) {
    //   menuItems.push(
    //     {
    //       key: 'list-item-fullscreen',
    //       dataTest: 'presentationFullscreen',
    //       label: formattedLabel(isFullscreen),
    //       icon: isFullscreen ? 'exit_fullscreen' : 'fullscreen',
    //       onClick: () => {
    //         handleToggleFullscreen(fullscreenRef);
    //         const newElement = (elementId === currentElement) ? '' : elementId;
    //         const newGroup = (elementGroup === currentGroup) ? '' : elementGroup;

    //         layoutContextDispatch({
    //           type: ACTIONS.SET_FULLSCREEN_ELEMENT,
    //           value: {
    //             element: newElement,
    //             group: newGroup,
    //           },
    //         });
    //       },
    //     },
    //     {
    //       key: 'list-item-fullscreen',
    //       dataTest: 'presentationFullscreen',
    //       label: formatteAppLabel(isFullscreenApp),
    //       icon: isFullscreenApp ? 'exit_fullscreen' : 'fullscreen',
    //       onClick: () => {
    //         handleToggleFullscreenApp()
    //       },
    //     }
    //   );
    // }

    const { isIos } = deviceInfo;
    const { isSafari } = browserInfo;

    const createSvgOfCurrentSlide = async (slideNum) => {
      const darkThemeState = AppService.isDarkThemeEnabled();
      AppService.setDarkTheme(false);

      try {
        const shapes = tldrawAPI.getCurrentPageShapes();
        const backgroundShape = shapes.find((s) => s.id === `shape:BG-${slideNum}`);
        const pollShape = shapes.find((shape) => shape.type === 'poll');
        const cleanShapes = shapes.filter(
          (s) => !s.id.startsWith('shape:BOUNDARY') && s.type !== 'poll'
        );
        const svgElem = await tldrawAPI.getSvg(cleanShapes.map(s => s.id));

        // Convert to PNG
        let width = svgElem?.width?.baseVal?.value ?? window.screen.width;
        let height = svgElem?.height?.baseVal?.value ?? window.screen.height;


        const MAX_SIZE = 4000;

        // ✅ If either dimension exceeds 5000, cap both to 5000
        if (width > MAX_SIZE / 2 || height > MAX_SIZE / 2) {
          svgElem.setAttribute('width', MAX_SIZE);
          svgElem.setAttribute('height', MAX_SIZE);
          svgElem.setAttribute('viewBox', `${-MAX_SIZE / 2} ${-MAX_SIZE / 2} ${MAX_SIZE} ${MAX_SIZE}`);
          width = MAX_SIZE;
          height = MAX_SIZE;
        }

        const pngData = await toPng(svgElem, { width, height, backgroundColor: '#FFF' });

        // restore theme
        AppService.setDarkTheme(darkThemeState);
        const presentationName = `${courseName ?? 'presentation_'}_${lessonName ?? ''}_${new Date().toISOString().replace(/[:.]/g, "-")}.pdf`;

        return {
          slideNum,
          imageData: pngData,
          presentationId,
          presentationName,
          // courseId:'691417df3f9a24040803530e',
          // courseSessionId:'69141a923f9a24040803603f'
          courseId: courseDetails?.course?.courseDetail?.courseId,
          courseSessionId:  courseDetails?.course?.courseDetail?.courseSessionId
        };

      } catch (e) {
        console.error("ERROR IN CREATE SVG:", e);
        AppService.setDarkTheme(darkThemeState);
        return null;
      }
    };


    if (allowSnapshotOfCurrentSlide) {
      menuItems.push(
        {
          key: 'list-item-screenshot',
          label: intl.formatMessage(intlMessages.snapshotLabel),
          dataTest: 'presentationSnapshot',
          // icon: 'video',
          customIcon: <SvgIcon iconName='snapshot' />,
          onClick: async () => {
            setState({
              loading: true,
              hasError: false,
            });

            toast(renderToastContent(), {
              hideProgressBar: true,
              autoClose: false,
              newestOnTop: true,
              closeOnClick: true,
              toastId: toastId.current,
            });

            // This is a workaround to a conflict of the
            // dark mode's styles and the html-to-image lib.
            // Issue:
            //  https://github.com/bubkoo/html-to-image/issues/370
            const darkThemeState = AppService.isDarkThemeEnabled();
            AppService.setDarkTheme(false);

            try {
              // filter shapes that are inside the slide
              const backgroundShape = tldrawAPI.getCurrentPageShapes().find((s) => s.id === `shape:BG-${slideNum}`);
              const shapes = tldrawAPI.getCurrentPageShapes();
              const pollShape = shapes.find((shape) => shape.type === 'poll');
              const svgElem = await tldrawAPI.getSvg(
                shapes
                  .filter((shape) => shape.type !== 'poll')
                  .map((shape) => shape.id),
              );
              // svgElem.setAttribute('width', backgroundShape.props.w);
              // svgElem.setAttribute('height', backgroundShape.props.h);
              // svgElem.setAttribute('viewBox', `1 1 ${backgroundShape.props.w} ${backgroundShape.props.h}`);
              if (pollShape) {
                const pollShapeElement = document.getElementById(pollShape.id);
                const pollShapeSvg = await toSvg(pollShapeElement);
                const pollShapeImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
                // pollShapeImage.setAttribute('href', pollShapeSvg);
                // pollShapeImage.setAttribute('width', pollShape.props.w);
                // pollShapeImage.setAttribute('height', pollShape.props.h);
                // pollShapeImage.setAttribute('x', pollShape.x);
                // pollShapeImage.setAttribute('y', pollShape.y);
                // svgElem.appendChild(pollShapeImage);
              }

              // workaround for ios
              if (isIos || isSafari) {
                const svgString = new XMLSerializer().serializeToString(svgElem);
                const blob = new Blob([svgString], { type: 'image/svg+xml' });

                const data = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = data;
                anchor.setAttribute(
                  'download',
                  `${elementName}_${meetingName}_${new Date().toISOString()}.svg`,
                );
                anchor.click();
              } else {
                const width = svgElem?.width?.baseVal?.value ?? window.screen.width;
                const height = svgElem?.height?.baseVal?.value ?? window.screen.height;

                const data = await toPng(svgElem, { width, height, backgroundColor: '#FFF' });

                const anchor = document.createElement('a');
                anchor.href = data;
                anchor.setAttribute(
                  'download',
                  `${elementName}_${meetingName}_${new Date().toISOString()}.png`,
                );
                anchor.click();
              }

              setState({
                loading: false,
                hasError: false,
              });
            } catch (e) {
              setState({
                loading: false,
                hasError: true,
              });

              logger.warn({
                logCode: 'presentation_snapshot_error',
                extraInfo: e,
              });
            } finally {
              // Workaround
              AppService.setDarkTheme(darkThemeState);
            }
          },
        },
      );
      // menuItems.push(
      //   {
      //     key: 'list-item-screenshot',
      //     label: 'Download presentation with annotation',
      //     dataTest: 'presentationSnapshot',
      //     icon: 'download',
      //     // customIcon: <SvgIcon iconName='snapshot' />,
      //     onClick: async () => {
      //       setState({
      //         loading: true,
      //         hasError: false,
      //       });

      //       // toast(renderToastContent(), {
      //       //   hideProgressBar: true,
      //       //   autoClose: false,
      //       //   newestOnTop: true,
      //       //   closeOnClick: true,
      //       //   toastId: toastId.current,
      //       // });

      //       toast(<Styled.Line>
      //         <Styled.ToastText>
      //           <span>
      //             Generating slides for PDF...
      //           </span>
      //         </Styled.ToastText>
      //         <Styled.StatusIcon>
      //           <Styled.ToastIcon

      //             loading={true}
      //             iconName={'blank'}
      //           />
      //         </Styled.StatusIcon>
      //       </Styled.Line>, {
      //         hideProgressBar: true,
      //         autoClose: false,
      //         newestOnTop: true,
      //         closeOnClick: true,
      //         toastId: presentationToastId.current,
      //       });
      //       generatePdfFromSlides()
      //     },
      //   },
      // );
    }

    const showVisibilityOption = currentUser?.presenter || hasWBAccess;

    showVisibilityOption && menuItems.push(
      {
        key: 'list-item-toolvisibility',
        dataTest: 'toolVisibility',
        label: formattedVisibilityLabel(isToolbarVisible),
        // icon: isToolbarVisible ? 'close' : 'pen_tool',pencil
        customIcon: isToolbarVisible ? <SvgIcon iconName={'hide_toolbar'} /> : <SvgIcon iconName={'pencil'} />,
        onClick: () => {
          setIsToolbarVisible(!isToolbarVisible);
        },
      },
    );

    // if (props.amIPresenter) {
    //   menuItems.push({
    //     key: 'list-item-load-shapes',
    //     dataTest: 'loadShapes',
    //     label: 'Load .tldr Data',
    //     icon: 'pen_tool',
    //     onClick: handleFileClick,
    //   });
    // }

    presentationDropdownItems.forEach((item, index) => {
      switch (item.type) {
        case PresentationDropdownItemType.OPTION:
          menuItems.push({
            key: `${item.id}-${index}`,
            label: item.label,
            icon: item.icon,
            onClick: item.onClick,
          });
          break;
        case PresentationDropdownItemType.SEPARATOR:
          menuItems.push({
            key: `${item.id}-${index}`,
            isSeparator: true,
          });
          break;
        default:
          break;
      }
    });

    return menuItems;
  }

  useEffect(() => {
    if (toast.isActive(toastId.current)) {
      toast.update(toastId.current, {
        render: renderToastContent(),
        hideProgressBar: state.loading,
        autoClose: state.loading ? false : 3000,
        newestOnTop: true,
        closeOnClick: true,
      });
    }
    if (dropdownRef.current) {
      document.activeElement.blur();
      dropdownRef.current.focus();
    }
  });

  const options = getAvailableOptions();

  if (options?.length === 0) {
    const undoCtrls = document.getElementById('TD-Styles')?.nextSibling;
    if (undoCtrls?.style) {
      undoCtrls.style = 'padding:0px';
    }
    const styleTool = document.getElementById('TD-Styles')?.parentNode;
    if (styleTool?.style) {
      styleTool.style = 'right:0px';
    }
    return null;
  }

  return (
    <Styled.Right id="WhiteboardOptionButton">
      <BBBMenu
        trigger={(
          <TooltipContainer title={intl.formatMessage(intlMessages.optionsLabel)}>
            <Styled.DropdownButton
              state={isDropdownOpen ? 'open' : 'closed'}
              aria-label={`${intl.formatMessage(intlMessages.whiteboardLabel)} ${intl.formatMessage(intlMessages.optionsLabel)}`}
              data-test="whiteboardOptionsButton"
              data-state={isDropdownOpen ? 'open' : 'closed'}
              onClick={() => {
                setIsDropdownOpen((isOpen) => !isOpen);
              }}
            >
              <SvgIcon iconName="whiteboardOptions" />
            </Styled.DropdownButton>
          </TooltipContainer>
        )}
        opts={{
          id: 'presentation-dropdown-menu',
          keepMounted: true,
          transitionDuration: 0,
          elevation: 3,
          getcontentanchorel: null,
          fullwidth: 'true',
          anchorOrigin: { vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' },
          transformOrigin: { vertical: 'top', horizontal: isRTL ? 'right' : 'left' },
          container: fullscreenRef,
        }}
        actions={options}
      />
      <input
        type="file"
        id="hiddenFileInput"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />
    </Styled.Right>
  );
};

PresentationMenu.propTypes = propTypes;

export default injectIntl(PresentationMenu);
